import type { ReactElement } from 'react';
import { useEffect } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType, Redirect } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getProductPageData } from 'services/getProductPageData';
import { AttributeName, PageType, getIsServerDesktop } from '@storefront/lib-global';
import { CmsSlotsProvider } from '@storefront/lib-global/context/cmsSlotsContext';
import { MasterLayout } from '@storefront/lib-global/globalComponents/masterLayout';
import { Meta } from '@storefront/lib-global/globalComponents/seo/Meta';
import { initializeApollo } from '@storefront/lib-global/services/gql/apolloClient';
import { logger } from '@storefront/lib-global/utils/logger';
import { setNewRelicAttributes } from '@storefront/lib-global/utils/newrelic';
import { setCacheTagHeaderByType } from '@storefront/lib-global/utils/setCacheTagHeader';
import { setCustomCacheHeader } from '@storefront/lib-global/utils/setCustomCacheHeader';

/* eslint-disable promise/prefer-await-to-then */
const PDPLayout = dynamic(() =>
  import('@storefront/lib-products/product-detail/components/PDPLayout').then((mod) => mod.PDPLayout),
);
/* eslint-disable promise/prefer-await-to-then */

export default function ProductPage({
  giftCard,
  handlebarComponents,
  products,
  product,
  pdpLayout,
  host,
  locale,
  carouselData,
  cmsSlotsData,
  isServerDesktop,
}: InferGetServerSidePropsType<typeof getServerSideProps>): ReactElement {
  const { query } = useRouter();

  useEffect(() => {
    setNewRelicAttributes(
      {
        [AttributeName.PRODUCT_ID]: giftCard?.key || product?.key,
        [AttributeName.PRODUCT_NAME]: giftCard?.name || [product?.styleName, product?.mediumColor].join(' ').trim(),
      },
      { interactionOnly: true },
    );
  }, [product, giftCard, query]);

  return (
    <>
      <Meta
        {...product}
        {...giftCard}
        isProduct
        isPrivate={product?.isPrivate}
        host={host}
        nbHits={products?.nbHits}
        pageSize={products?.hitsPerPage}
        productKey={product?.key}
        giftCardKey={giftCard?.key}
        pageType={PageType.PDP}
        assignedLocales={product?.assignedStoresSelectionCategoryLocales}
      />
      <CmsSlotsProvider cmsSlotsData={cmsSlotsData}>
        <MasterLayout>
          <PDPLayout
            pdpLayout={pdpLayout}
            product={product}
            giftCard={giftCard}
            carouselData={carouselData}
            handlebarComponents={handlebarComponents}
            locale={locale}
            isServerDesktop={isServerDesktop}
          />
        </MasterLayout>
      </CmsSlotsProvider>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ query, locale, req, res }) => {
  const { host } = req.headers;

  const apolloClient = initializeApollo(req, res);
  const isServerDesktop = getIsServerDesktop(req);

  const [{ notFound, props, redirect }, translations] = await Promise.all([
    getProductPageData(apolloClient, {
      locale,
      slug: query.slug as string[] | undefined,
      query,
    }),
    serverSideTranslations(locale || 'en', [
      'lib-content-common',
      'lib-global-common',
      'lib-products-common',
      'lib-appointments-common',
      'lib-csa-common',
      'lib-global-countries-common',
    ]),
  ]);

  logger.debug({ notFound, props, redirect }, 'getProductPageData');

  setCacheTagHeaderByType(
    res,
    locale,
    { product: props?.product, giftCard: props?.giftCard },
    query.slug as string[] | undefined,
  );

  if (notFound) {
    return {
      notFound,
    };
  }
  if (redirect) {
    return {
      redirect: redirect as Redirect,
    };
  }

  setCustomCacheHeader(res, { noStore: true, noCache: true, mustRevalidate: true });

  return {
    props: {
      ...props,
      ...translations,
      host,
      locale,
      isServerDesktop,
    },
  };
};
